import axios from 'axios';
import {
  getFirebaseToken,
  waitForAuthState,
} from '../modules/erp/utils/getFirebaseToken';

const PRODUCTS_BASE_URL = 'https://modularobjects.com/products';
const PRODUCTS_API_ORIGIN = new URL(PRODUCTS_BASE_URL).origin;
const AUTH_RETRY_MARKER = '_productsAuthRetried';

export const productsPublicApi = axios.create({
  baseURL: PRODUCTS_BASE_URL,
  withCredentials: false,
});

export const productsAdminApi = axios.create({
  baseURL: PRODUCTS_BASE_URL,
  withCredentials: false,
});

function getRequestOrigin(config) {
  const baseURL = config?.baseURL || PRODUCTS_BASE_URL;
  return new URL(config?.url || '', baseURL).origin;
}

function isProductsApiRequest(config) {
  try {
    return getRequestOrigin(config) === PRODUCTS_API_ORIGIN;
  } catch {
    return false;
  }
}

function removeAuthorizationHeader(headers) {
  if (!headers) return;

  if (typeof headers.delete === 'function') {
    headers.delete('Authorization');
  } else {
    Object.keys(headers).forEach((headerName) => {
      if (headerName.toLowerCase() === 'authorization') {
        delete headers[headerName];
      }
    });
  }
}

function setAuthorizationHeader(config, token) {
  config.headers = config.headers || {};
  removeAuthorizationHeader(config.headers);

  if (typeof config.headers.set === 'function') {
    config.headers.set('Authorization', `Bearer ${token}`);
  } else {
    config.headers.Authorization = `Bearer ${token}`;
  }
}

productsPublicApi.interceptors.request.use((config) => {
  removeAuthorizationHeader(config.headers);
  return config;
});

productsAdminApi.interceptors.request.use(async (config) => {
  if (!isProductsApiRequest(config)) {
    throw new Error('productsAdminApi only supports the Products API origin.');
  }

  if (config[AUTH_RETRY_MARKER]) {
    return config;
  }

  const token = await getFirebaseToken();
  setAuthorizationHeader(config, token);
  return config;
});

let refreshPromise = null;

function getForcedFirebaseToken() {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const user = await waitForAuthState();

      if (!user) {
        throw new Error('User is not signed in.');
      }

      return user.getIdToken(true);
    })().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

productsAdminApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (
      error?.response?.status !== 401 ||
      !originalRequest ||
      originalRequest[AUTH_RETRY_MARKER] ||
      !isProductsApiRequest(originalRequest)
    ) {
      return Promise.reject(error);
    }

    originalRequest[AUTH_RETRY_MARKER] = true;

    const token = await getForcedFirebaseToken();
    setAuthorizationHeader(originalRequest, token);

    return productsAdminApi.request(originalRequest);
  }
);
